import { ReactNode, useId } from 'react';

import { Stack, Typography, StackProps, TypographyProps } from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../../theme/generated/tokens';
import { CssVarDefinitions } from '../../../theme/types';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';
import { Inset, InsetProps } from '../../containers/Inset';
import { RowInset, RowInsetProps } from '../../containers/RowInset';
import { mergeStyles, cardVars, toDsVarsDefinition } from '../../variables';

const classNamesFactory = classNamesFor('LayoutCard');

const classNames = {
  root: classNamesFactory.root(),
  header: classNamesFactory.element('header'),
  content: classNamesFactory.element('content'),
};

const defaultCssVars = toDsVarsDefinition({
  inset: { pt: '16px', pr: '16px', pb: '16px', pl: '16px' },
  divider: {
    color: tokens.theme.border.structural.neutral,
  },
});

export type LayoutCardProps = {
  /**
   * Optional title displayed inside the card header.
   */
  title?: ReactNode;
  /**
   * Optional content rendered in the right-side slot of the card header.
   * Intended for card-level actions (buttons, menus, etc.).
   * The header is shown whenever `title` or `headerEndContent` is provided.
   */
  headerEndContent?: ReactNode;
  /**
   * If true, shows the divider at the bottom of the header.
   */
  headerDivider?: boolean;
  /**
   * Whether to apply padding inside the card body (inherits from inset CSS vars).
   * @default true
   */
  padding?: boolean;
  /**
   * styling, applied to the root Stack wrapper.
   */
  sx?: SxProps;
  slotProps?: {
    root?: Partial<StackProps>;
    header?: Partial<RowInsetProps>;
    content?: Partial<InsetProps>;
    title?: Partial<TypographyProps>;
  };
  /**
   * CSS variables applied to the card root.
   * Use to provide CSS variables for descendants of the card.
   * @example cssVars={{'--someVariable': '20px'}}
   */
  cssVars?: CssVarDefinitions;
  children?: ReactNode;
  className?: string;
  testId?: string;
};

/**
 * Card component for building page layouts with the title rendered inside the card.
 *
 * Differs from LayoutCardV1 in that the title appears as a header section within
 * the white card body, rather than floating above it against the page background.
 */
export const LayoutCard = ({
  title,
  headerEndContent,
  padding = true,
  headerDivider,
  children,
  className,
  testId = 'layout-card-v2',
  sx,
  slotProps,
  cssVars,
}: LayoutCardProps) => {
  const titleId = useId();
  const hasHeader = !!title || !!headerEndContent;

  return (
    <Stack
      aria-labelledby={title ? slotProps?.title?.id ?? titleId : undefined}
      data-testid={testId}
      style={mergeStyles(defaultCssVars, slotProps?.root?.style, cssVars)}
      {...slotProps?.root}
      className={clsx(classNames.root, className, slotProps?.root?.className)}
      sx={mergeSx(
        {
          backgroundColor: cardVars.refs.backgroundColor,
          borderRadius: cardVars.refs.borderRadius,
          border: `1px solid ${tokens.theme.border.structural.neutral}`,
        },
        sx,
        slotProps?.root?.sx
      )}
    >
      {hasHeader && (
        <RowInset
          startContent={
            title ? (
              <Typography
                id={titleId}
                variant="A1"
                color={tokens.theme.fg.structural.primary}
                data-testid="layout-card-v2-title"
                {...slotProps?.title}
              >
                {title}
              </Typography>
            ) : undefined
          }
          endContent={headerEndContent}
          dividerBottom={headerDivider}
          px
          py={16}
          alignItems="center"
          {...slotProps?.header}
          className={clsx(classNames.header, slotProps?.header?.className)}
          sx={mergeSx({ flexShrink: 0, gap: '8px' }, slotProps?.header?.sx)}
          slotProps={{
            ...slotProps?.header?.slotProps,
            startContentWrapper: {
              ...slotProps?.header?.slotProps?.startContentWrapper,
              sx: mergeSx(
                { flex: 1, minWidth: 0 },
                slotProps?.header?.slotProps?.startContentWrapper?.sx
              ),
            },
          }}
        />
      )}
      <Inset
        px={padding}
        py={padding}
        pt={padding && !hasHeader}
        pb={padding}
        {...slotProps?.content}
        className={clsx(classNames.content, slotProps?.content?.className)}
        sx={mergeSx({ flex: 1, overflow: 'hidden' }, slotProps?.content?.sx)}
      >
        {children}
      </Inset>
    </Stack>
  );
};

LayoutCard.classNames = classNames;
